/**
 * LinkedIn CSV Parsers
 *
 * Parses the exact CSV formats from LinkedIn data exports.
 * Handles quoted fields, multi-line content, and various date formats.
 */

import type {
  Position,
  Education,
  Connection,
  JobApplication,
  Profile,
  Article,
  Message,
  Publication,
  Cause,
  Project,
  Recommendation,
  Share,
  Comment,
} from '../types.js';

/**
 * Parse CSV content into rows with proper handling of:
 * - Quoted fields with commas
 * - Multi-line quoted fields
 * - Escaped quotes (doubled quotes)
 */
function parseCSV(csv: string): string[][] {
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentField = '';
  let inQuotes = false;
  let i = 0;

  while (i < csv.length) {
    const char = csv[i];
    const nextChar = csv[i + 1];

    if (inQuotes) {
      if (char === '"' && nextChar === '"') {
        // Escaped quote
        currentField += '"';
        i += 2;
      } else if (char === '"') {
        // End of quoted field
        inQuotes = false;
        i++;
      } else {
        currentField += char;
        i++;
      }
    } else {
      if (char === '"') {
        // Start of quoted field
        inQuotes = true;
        i++;
      } else if (char === ',') {
        // Field separator
        currentRow.push(currentField.trim());
        currentField = '';
        i++;
      } else if (char === '\n' || (char === '\r' && nextChar === '\n')) {
        // Row separator
        currentRow.push(currentField.trim());
        if (currentRow.some(field => field !== '')) {
          rows.push(currentRow);
        }
        currentRow = [];
        currentField = '';
        i += char === '\r' ? 2 : 1;
      } else if (char === '\r') {
        // Handle standalone \r
        currentRow.push(currentField.trim());
        if (currentRow.some(field => field !== '')) {
          rows.push(currentRow);
        }
        currentRow = [];
        currentField = '';
        i++;
      } else {
        currentField += char;
        i++;
      }
    }
  }

  // Handle last field/row
  if (currentField || currentRow.length > 0) {
    currentRow.push(currentField.trim());
    if (currentRow.some(field => field !== '')) {
      rows.push(currentRow);
    }
  }

  return rows;
}

/**
 * Parse CSV with header detection and return array of objects
 */
function parseCSVWithHeaders<T>(csv: string, headerMap: Record<string, keyof T>): Partial<T>[] {
  const rows = parseCSV(csv);
  if (rows.length === 0) return [];

  // Find header row (skip notes/metadata rows)
  let headerRowIndex = 0;
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    // Check if this row looks like headers (contains expected column names)
    const rowStr = row.join(',').toLowerCase();
    if (Object.keys(headerMap).some(h => rowStr.includes(h.toLowerCase()))) {
      headerRowIndex = i;
      break;
    }
  }

  const headers = rows[headerRowIndex];
  const columnIndices: Record<keyof T, number> = {} as Record<keyof T, number>;

  // Map header names to column indices
  headers.forEach((header, index) => {
    const normalizedHeader = header.toLowerCase().trim();
    for (const [csvHeader, propName] of Object.entries(headerMap)) {
      if (normalizedHeader === csvHeader.toLowerCase()) {
        columnIndices[propName as keyof T] = index;
      }
    }
  });

  // Parse data rows
  const results: Partial<T>[] = [];
  for (let i = headerRowIndex + 1; i < rows.length; i++) {
    const row = rows[i];
    const obj: Partial<T> = {};

    for (const [propName, idx] of Object.entries(columnIndices)) {
      if (typeof idx === 'number' && idx < row.length) {
        (obj as any)[propName] = row[idx];
      }
    }

    // Only add if we got some data
    if (Object.keys(obj).length > 0) {
      results.push(obj);
    }
  }

  return results;
}

/**
 * Parse LinkedIn date format: "Mon YYYY" (e.g., "Aug 2020") or "YYYY"
 */
function parseLinkedInDate(dateStr: string | undefined): Date | null {
  if (!dateStr || dateStr.trim() === '') return null;

  const str = dateStr.trim();

  // Format: "Mon YYYY" (e.g., "Aug 2020")
  const monthYearMatch = str.match(/^([A-Za-z]+)\s+(\d{4})$/);
  if (monthYearMatch) {
    const monthNames: Record<string, number> = {
      jan: 0, january: 0,
      feb: 1, february: 1,
      mar: 2, march: 2,
      apr: 3, april: 3,
      may: 4,
      jun: 5, june: 5,
      jul: 6, july: 6,
      aug: 7, august: 7,
      sep: 8, sept: 8, september: 8,
      oct: 9, october: 9,
      nov: 10, november: 10,
      dec: 11, december: 11,
    };
    const month = monthNames[monthYearMatch[1].toLowerCase()];
    const year = parseInt(monthYearMatch[2], 10);
    if (month !== undefined && !isNaN(year)) {
      return new Date(year, month, 1);
    }
  }

  // Format: "YYYY" (e.g., "2020")
  const yearMatch = str.match(/^(\d{4})$/);
  if (yearMatch) {
    return new Date(parseInt(yearMatch[1], 10), 0, 1);
  }

  // Format: "DD Mon YYYY" (e.g., "01 Jan 2026")
  const dayMonthYearMatch = str.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/);
  if (dayMonthYearMatch) {
    const day = parseInt(dayMonthYearMatch[1], 10);
    const monthNames: Record<string, number> = {
      jan: 0, january: 0, feb: 1, february: 1, mar: 2, march: 2,
      apr: 3, april: 3, may: 4, jun: 5, june: 5, jul: 6, july: 6,
      aug: 7, august: 7, sep: 8, sept: 8, september: 8,
      oct: 9, october: 9, nov: 10, november: 10, dec: 11, december: 11,
    };
    const month = monthNames[dayMonthYearMatch[2].toLowerCase()];
    const year = parseInt(dayMonthYearMatch[3], 10);
    if (month !== undefined && !isNaN(year) && !isNaN(day)) {
      return new Date(year, month, day);
    }
  }

  // Format: "Mon D, YYYY" (e.g., "Jan 3, 1989")
  const monthDayYearMatch = str.match(/^([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})$/);
  if (monthDayYearMatch) {
    const monthNames: Record<string, number> = {
      jan: 0, january: 0, feb: 1, february: 1, mar: 2, march: 2,
      apr: 3, april: 3, may: 4, jun: 5, june: 5, jul: 6, july: 6,
      aug: 7, august: 7, sep: 8, sept: 8, september: 8,
      oct: 9, october: 9, nov: 10, november: 10, dec: 11, december: 11,
    };
    const month = monthNames[monthDayYearMatch[1].toLowerCase()];
    const day = parseInt(monthDayYearMatch[2], 10);
    const year = parseInt(monthDayYearMatch[3], 10);
    if (month !== undefined && !isNaN(year) && !isNaN(day)) {
      return new Date(year, month, day);
    }
  }

  // Format: "M/D/YY, H:MM AM/PM" (e.g., "11/23/21, 11:32 AM")
  const usDateTimeMatch = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (usDateTimeMatch) {
    const month = parseInt(usDateTimeMatch[1], 10) - 1;
    const day = parseInt(usDateTimeMatch[2], 10);
    let year = parseInt(usDateTimeMatch[3], 10);
    if (year < 100) {
      year += year > 50 ? 1900 : 2000;
    }
    return new Date(year, month, day);
  }

  // Format: "YYYY-MM-DD HH:MM:SS UTC" (e.g., "2026-01-02 03:34:04 UTC")
  const isoDateMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDateMatch) {
    return new Date(str);
  }

  return null;
}

/**
 * Parse LinkedIn Positions.csv
 * Headers: Company Name,Title,Description,Location,Started On,Finished On
 */
export function parsePositions(csv: string): Position[] {
  const headerMap = {
    'Company Name': 'companyName' as const,
    'Title': 'title' as const,
    'Description': 'description' as const,
    'Location': 'location' as const,
    'Started On': 'rawStartDate' as const,
    'Finished On': 'rawEndDate' as const,
  };

  const raw = parseCSVWithHeaders<Position & { rawStartDate?: string; rawEndDate?: string }>(csv, headerMap);

  return raw.map(r => {
    const position: Position = {
      companyName: r.companyName || '',
      title: r.title || '',
      description: r.description || '',
      location: r.location || '',
      startedOn: parseLinkedInDate(r.rawStartDate),
      finishedOn: parseLinkedInDate(r.rawEndDate),
      rawStartDate: r.rawStartDate,
      rawEndDate: r.rawEndDate,
      isSideBusiness: detectSideBusiness(r.title || '', r.description || '', r.companyName || ''),
    };
    return position;
  }).filter(p => p.companyName || p.title);
}

/**
 * Detect if a position appears to be a side business
 */
function detectSideBusiness(title: string, description: string, company: string): boolean {
  const combined = `${title} ${description} ${company}`.toLowerCase();
  const indicators = [
    'co-founder', 'cofounder', 'founder', 'owner', 'ceo',
    'side project', 'startup', 'my company', 'own business',
    'freelance', 'consultant', 'self-employed', 'entrepreneur',
    'independent', 'bootstrapped', 'personal venture',
  ];
  return indicators.some(ind => combined.includes(ind));
}

/**
 * Parse LinkedIn Education.csv
 * Headers: School Name,Start Date,End Date,Notes,Degree Name,Activities
 */
export function parseEducation(csv: string): Education[] {
  const headerMap = {
    'School Name': 'schoolName' as const,
    'Start Date': 'rawStartDate' as const,
    'End Date': 'rawEndDate' as const,
    'Notes': 'notes' as const,
    'Degree Name': 'degreeName' as const,
    'Activities': 'activities' as const,
  };

  const raw = parseCSVWithHeaders<Education & { rawStartDate?: string; rawEndDate?: string }>(csv, headerMap);

  return raw.map(r => ({
    schoolName: r.schoolName || '',
    startDate: parseLinkedInDate(r.rawStartDate),
    endDate: parseLinkedInDate(r.rawEndDate),
    notes: r.notes || '',
    degreeName: r.degreeName || '',
    activities: r.activities || '',
    rawStartDate: r.rawStartDate,
    rawEndDate: r.rawEndDate,
  })).filter(e => e.schoolName);
}

/**
 * Parse LinkedIn Connections.csv
 * Headers: First Name,Last Name,URL,Email Address,Company,Position,Connected On
 * Note: File may have notes at the top before headers
 */
export function parseConnections(csv: string): Connection[] {
  const headerMap = {
    'First Name': 'firstName' as const,
    'Last Name': 'lastName' as const,
    'URL': 'url' as const,
    'Email Address': 'emailAddress' as const,
    'Company': 'company' as const,
    'Position': 'position' as const,
    'Connected On': 'rawConnectedOn' as const,
  };

  const raw = parseCSVWithHeaders<Connection & { rawConnectedOn?: string }>(csv, headerMap);

  return raw.map(r => ({
    firstName: r.firstName || '',
    lastName: r.lastName || '',
    url: r.url || '',
    emailAddress: r.emailAddress || '',
    company: r.company || '',
    position: r.position || '',
    connectedOn: parseLinkedInDate(r.rawConnectedOn),
    rawConnectedOn: r.rawConnectedOn,
  })).filter(c => c.firstName || c.lastName || c.url);
}

/**
 * Parse LinkedIn Job Applications.csv
 * Headers: Application Date,Contact Email,Contact Phone Number,Company Name,Job Title,Job Url,Resume Name,Question And Answers
 */
export function parseJobApplications(csv: string): JobApplication[] {
  const headerMap = {
    'Application Date': 'rawApplicationDate' as const,
    'Contact Email': 'contactEmail' as const,
    'Contact Phone Number': 'contactPhone' as const,
    'Company Name': 'companyName' as const,
    'Job Title': 'jobTitle' as const,
    'Job Url': 'jobUrl' as const,
    'Resume Name': 'resumeName' as const,
    'Question And Answers': 'questionAndAnswers' as const,
  };

  const raw = parseCSVWithHeaders<JobApplication & { rawApplicationDate?: string }>(csv, headerMap);

  return raw.map(r => {
    const parsedQA = parseQuestionAnswers(r.questionAndAnswers || '');
    const requiresSponsorship = detectSponsorshipRequired(parsedQA, r.questionAndAnswers || '');
    const authorizedToWork = detectWorkAuthorization(parsedQA);

    return {
      applicationDate: parseLinkedInDate(r.rawApplicationDate),
      contactEmail: r.contactEmail || '',
      contactPhone: r.contactPhone || '',
      companyName: r.companyName || '',
      jobTitle: r.jobTitle || '',
      jobUrl: r.jobUrl || '',
      resumeName: r.resumeName || '',
      questionAndAnswers: r.questionAndAnswers || '',
      parsedQA,
      requiresSponsorship,
      authorizedToWork,
      rawApplicationDate: r.rawApplicationDate,
    };
  }).filter(j => j.companyName || j.jobTitle || j.jobUrl);
}

/**
 * Parse the question and answers string from job applications
 * Format: "Question1:Answer1 | Question2:Answer2 | ..."
 */
function parseQuestionAnswers(qaString: string): Record<string, string> {
  const result: Record<string, string> = {};
  if (!qaString) return result;

  const pairs = qaString.split(' | ');
  for (const pair of pairs) {
    const colonIndex = pair.indexOf(':');
    if (colonIndex > 0) {
      const question = pair.substring(0, colonIndex).trim();
      const answer = pair.substring(colonIndex + 1).trim();
      result[question] = answer;
    }
  }
  return result;
}

/**
 * Detect if the job application indicates sponsorship is required
 */
function detectSponsorshipRequired(parsedQA: Record<string, string>, rawQA: string): boolean {
  const combined = rawQA.toLowerCase();
  const qaEntries = Object.entries(parsedQA);

  // Check for explicit sponsorship questions
  for (const [question, answer] of qaEntries) {
    const q = question.toLowerCase();
    const a = answer.toLowerCase();

    if (
      (q.includes('sponsor') || q.includes('visa status') || q.includes('h-1b')) &&
      (a === 'yes' || a.includes('yes'))
    ) {
      return true;
    }

    // "Will you require sponsorship" - Yes answer
    if (q.includes('require') && q.includes('sponsor') && a === 'yes') {
      return true;
    }

    // "Do you now or in the future require visa sponsorship" - Yes answer
    if (q.includes('future') && q.includes('sponsor') && a === 'yes') {
      return true;
    }
  }

  // Check raw string for patterns
  if (
    combined.includes('require sponsorship') && combined.includes(':yes') ||
    combined.includes('visa sponsorship') && combined.includes(':yes')
  ) {
    return true;
  }

  return false;
}

/**
 * Detect if the applicant is authorized to work
 */
function detectWorkAuthorization(parsedQA: Record<string, string>): boolean {
  for (const [question, answer] of Object.entries(parsedQA)) {
    const q = question.toLowerCase();
    const a = answer.toLowerCase();

    if (
      (q.includes('legally authorized') || q.includes('eligible to work') || q.includes('authorized to work')) &&
      (a === 'yes' || a.includes('yes'))
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Parse LinkedIn Profile.csv
 * Headers: First Name,Last Name,Maiden Name,Address,Birth Date,Headline,Summary,Industry,Zip Code,Geo Location,Twitter Handles,Websites,Instant Messengers
 */
export function parseProfile(csv: string): Profile {
  const headerMap = {
    'First Name': 'firstName' as const,
    'Last Name': 'lastName' as const,
    'Maiden Name': 'maidenName' as const,
    'Address': 'address' as const,
    'Birth Date': 'rawBirthDate' as const,
    'Headline': 'headline' as const,
    'Summary': 'summary' as const,
    'Industry': 'industry' as const,
    'Zip Code': 'zipCode' as const,
    'Geo Location': 'geoLocation' as const,
    'Twitter Handles': 'twitterHandlesRaw' as const,
    'Websites': 'websitesRaw' as const,
    'Instant Messengers': 'instantMessengersRaw' as const,
  };

  interface RawProfile extends Partial<Profile> {
    rawBirthDate?: string;
    twitterHandlesRaw?: string;
    websitesRaw?: string;
    instantMessengersRaw?: string;
  }

  const raw = parseCSVWithHeaders<RawProfile>(csv, headerMap);

  if (raw.length === 0) {
    return {
      firstName: '',
      lastName: '',
      maidenName: '',
      address: '',
      birthDate: null,
      headline: '',
      summary: '',
      industry: '',
      zipCode: '',
      geoLocation: '',
      twitterHandles: [],
      websites: [],
      instantMessengers: [],
    };
  }

  const r = raw[0];
  return {
    firstName: r.firstName || '',
    lastName: r.lastName || '',
    maidenName: r.maidenName || '',
    address: r.address || '',
    birthDate: parseLinkedInDate(r.rawBirthDate),
    headline: r.headline || '',
    summary: r.summary || '',
    industry: r.industry || '',
    zipCode: r.zipCode || '',
    geoLocation: r.geoLocation || '',
    twitterHandles: parseArrayField(r.twitterHandlesRaw),
    websites: parseArrayField(r.websitesRaw),
    instantMessengers: parseArrayField(r.instantMessengersRaw),
    rawBirthDate: r.rawBirthDate,
  };
}

/**
 * Parse array fields like "[value1, value2]" or "value1,value2"
 */
function parseArrayField(field: string | undefined): string[] {
  if (!field) return [];

  // Handle [value] format
  let cleaned = field.trim();
  if (cleaned.startsWith('[') && cleaned.endsWith(']')) {
    cleaned = cleaned.slice(1, -1);
  }

  return cleaned.split(',').map(s => s.trim()).filter(s => s.length > 0);
}

/**
 * Parse LinkedIn Messages.csv
 * Headers: CONVERSATION ID,CONVERSATION TITLE,FROM,SENDER PROFILE URL,TO,RECIPIENT PROFILE URLS,DATE,SUBJECT,CONTENT,FOLDER,ATTACHMENTS,IS MESSAGE DRAFT,IS CONVERSATION DRAFT
 */
export function parseMessages(csv: string): Message[] {
  const headerMap = {
    'CONVERSATION ID': 'conversationId' as const,
    'CONVERSATION TITLE': 'conversationTitle' as const,
    'FROM': 'from' as const,
    'SENDER PROFILE URL': 'senderProfileUrl' as const,
    'TO': 'to' as const,
    'RECIPIENT PROFILE URLS': 'recipientProfileUrls' as const,
    'DATE': 'rawDate' as const,
    'SUBJECT': 'subject' as const,
    'CONTENT': 'content' as const,
    'FOLDER': 'folder' as const,
    'ATTACHMENTS': 'attachments' as const,
    'IS MESSAGE DRAFT': 'isMessageDraftRaw' as const,
    'IS CONVERSATION DRAFT': 'isConversationDraftRaw' as const,
  };

  interface RawMessage extends Partial<Message> {
    rawDate?: string;
    isMessageDraftRaw?: string;
    isConversationDraftRaw?: string;
  }

  const raw = parseCSVWithHeaders<RawMessage>(csv, headerMap);

  return raw.map(r => ({
    conversationId: r.conversationId || '',
    conversationTitle: r.conversationTitle || '',
    from: r.from || '',
    senderProfileUrl: r.senderProfileUrl || '',
    to: r.to || '',
    recipientProfileUrls: r.recipientProfileUrls || '',
    date: parseLinkedInDate(r.rawDate),
    subject: r.subject || '',
    content: r.content || '',
    folder: r.folder || '',
    attachments: r.attachments || '',
    isMessageDraft: r.isMessageDraftRaw?.toLowerCase() === 'yes',
    isConversationDraft: r.isConversationDraftRaw?.toLowerCase() === 'yes',
    rawDate: r.rawDate,
  })).filter(m => m.content || m.subject);
}

/**
 * Parse LinkedIn Publications.csv
 * Headers: Name,Published On,Description,Publisher,Url
 */
export function parsePublications(csv: string): Publication[] {
  const headerMap = {
    'Name': 'name' as const,
    'Published On': 'rawPublishedOn' as const,
    'Description': 'description' as const,
    'Publisher': 'publisher' as const,
    'Url': 'url' as const,
  };

  const raw = parseCSVWithHeaders<Publication & { rawPublishedOn?: string }>(csv, headerMap);

  return raw.map(r => ({
    name: r.name || '',
    publishedOn: parseLinkedInDate(r.rawPublishedOn),
    description: r.description || '',
    publisher: r.publisher || '',
    url: r.url || '',
    rawPublishedOn: r.rawPublishedOn,
  })).filter(p => p.name);
}

/**
 * Parse LinkedIn Causes You Care About.csv
 * Headers: Supported Cause
 */
export function parseCauses(csv: string): Cause[] {
  const headerMap = {
    'Supported Cause': 'name' as const,
  };

  const raw = parseCSVWithHeaders<Cause>(csv, headerMap);
  return raw.filter(c => c.name).map(c => ({ name: c.name || '' }));
}

/**
 * Parse LinkedIn Projects.csv
 * Headers: Title,Description,Url,Started On,Finished On
 */
export function parseProjects(csv: string): Project[] {
  const headerMap = {
    'Title': 'title' as const,
    'Description': 'description' as const,
    'Url': 'url' as const,
    'Started On': 'rawStartDate' as const,
    'Finished On': 'rawEndDate' as const,
  };

  const raw = parseCSVWithHeaders<Project & { rawStartDate?: string; rawEndDate?: string }>(csv, headerMap);

  return raw.map(r => ({
    title: r.title || '',
    description: r.description || '',
    url: r.url || '',
    startedOn: parseLinkedInDate(r.rawStartDate),
    finishedOn: parseLinkedInDate(r.rawEndDate),
    rawStartDate: r.rawStartDate,
    rawEndDate: r.rawEndDate,
  })).filter(p => p.title);
}

/**
 * Parse LinkedIn Recommendations_Received.csv
 * Headers: First Name,Last Name,Company,Job Title,Text,Creation Date,Status
 */
export function parseRecommendations(csv: string): Recommendation[] {
  const headerMap = {
    'First Name': 'firstName' as const,
    'Last Name': 'lastName' as const,
    'Company': 'company' as const,
    'Job Title': 'jobTitle' as const,
    'Text': 'text' as const,
    'Creation Date': 'rawCreationDate' as const,
    'Status': 'status' as const,
  };

  const raw = parseCSVWithHeaders<Recommendation & { rawCreationDate?: string }>(csv, headerMap);

  return raw.map(r => ({
    firstName: r.firstName || '',
    lastName: r.lastName || '',
    company: r.company || '',
    jobTitle: r.jobTitle || '',
    text: r.text || '',
    creationDate: parseLinkedInDate(r.rawCreationDate),
    status: r.status || '',
    rawCreationDate: r.rawCreationDate,
  })).filter(r => r.text);
}

/**
 * Parse LinkedIn Article HTML files
 * Extracts title, content, and dates from HTML
 */
export function parseArticle(html: string, _filename?: string): Article {
  // Extract title from <title> or <h1>
  const titleMatch = html.match(/<title>([^<]*)<\/title>/i) ||
                     html.match(/<h1[^>]*><a[^>]*>([^<]*)<\/a><\/h1>/i) ||
                     html.match(/<h1[^>]*>([^<]*)<\/h1>/i);
  const title = titleMatch ? decodeHTMLEntities(titleMatch[1]) : '';

  // Extract created date
  const createdMatch = html.match(/class="created"[^>]*>Created on ([^<]*)</i);
  const createdOn = createdMatch ? parseLinkedInDate(createdMatch[1]) : null;

  // Extract published date
  const publishedMatch = html.match(/class="published"[^>]*>Published on ([^<]*)</i);
  const publishedOn = publishedMatch ? parseLinkedInDate(publishedMatch[1]) : null;

  // Extract URL
  const urlMatch = html.match(/<h1[^>]*><a href="([^"]*)">/i);
  const url = urlMatch ? urlMatch[1] : '';

  // Extract content (remove HTML tags)
  const contentMatch = html.match(/<div>(.+)<\/div>/s);
  let content = '';
  if (contentMatch) {
    content = contentMatch[1]
      .replace(/<[^>]+>/g, ' ')  // Remove HTML tags
      .replace(/\s+/g, ' ')      // Normalize whitespace
      .trim();
    content = decodeHTMLEntities(content);
  }

  return {
    title,
    content,
    createdOn,
    publishedOn,
    url,
    rawHtml: html,
  };
}

/**
 * Decode HTML entities
 */
function decodeHTMLEntities(text: string): string {
  const entities: Record<string, string> = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&nbsp;': ' ',
    '&ldquo;': '"',
    '&rdquo;': '"',
    '&lsquo;': "'",
    '&rsquo;': "'",
    '&mdash;': '-',
    '&ndash;': '-',
    '&#x200B;': '', // Zero-width space
    '&hellip;': '...',
  };

  let result = text;
  for (const [entity, char] of Object.entries(entities)) {
    result = result.replace(new RegExp(entity, 'g'), char);
  }

  // Handle numeric entities
  result = result.replace(/&#(\d+);/g, (_, code) => String.fromCharCode(parseInt(code, 10)));
  result = result.replace(/&#x([0-9a-fA-F]+);/g, (_, code) => String.fromCharCode(parseInt(code, 16)));

  return result;
}

/**
 * Parse Job Applicant Saved Screening Question Responses.csv
 * Headers: Question,Answer
 * Returns a map of questions to answers
 */
export function parseScreeningResponses(csv: string): Record<string, string> {
  const headerMap = {
    'Question': 'question' as const,
    'Answer': 'answer' as const,
  };

  interface QA { question?: string; answer?: string }
  const raw = parseCSVWithHeaders<QA>(csv, headerMap);

  const result: Record<string, string> = {};
  for (const r of raw) {
    if (r.question) {
      result[r.question] = r.answer || '';
    }
  }
  return result;
}

/**
 * Parse LinkedIn Shares.csv (posts/status updates)
 * Headers: Date,ShareLink,ShareCommentary,SharedUrl,MediaUrl,Visibility
 */
export function parseShares(csv: string): Share[] {
  const headerMap = {
    'Date': 'rawDate' as const,
    'ShareLink': 'shareLink' as const,
    'ShareCommentary': 'shareCommentary' as const,
    'SharedUrl': 'sharedUrl' as const,
    'MediaUrl': 'mediaUrl' as const,
    'Visibility': 'visibility' as const,
  };

  const raw = parseCSVWithHeaders<Share & { rawDate?: string }>(csv, headerMap);

  return raw.map(r => ({
    date: parseLinkedInDate(r.rawDate),
    shareLink: r.shareLink || '',
    shareCommentary: r.shareCommentary || '',
    sharedUrl: r.sharedUrl || '',
    mediaUrl: r.mediaUrl || '',
    visibility: r.visibility || '',
    rawDate: r.rawDate,
  })).filter(s => s.shareCommentary || s.sharedUrl); // Only keep shares with content
}

/**
 * Parse LinkedIn Comments.csv
 * Headers: Date,Link,Message
 */
export function parseComments(csv: string): Comment[] {
  const headerMap = {
    'Date': 'rawDate' as const,
    'Link': 'link' as const,
    'Message': 'message' as const,
  };

  const raw = parseCSVWithHeaders<Comment & { rawDate?: string }>(csv, headerMap);

  return raw.map(r => ({
    date: parseLinkedInDate(r.rawDate),
    link: r.link || '',
    message: r.message || '',
    rawDate: r.rawDate,
  })).filter(c => c.message); // Only keep comments with content
}
